/**
 * @architecture-header 1
 * @layer shared
 * @domain demo
 * @responsibility Supply the deterministic Vispo product dataset used when the host enables demo mode.
 * @state-owner immutable module constants
 * @allowed-imports shared view model types
 * @forbidden network; localStorage; production authorization
 * @public-api workspace modules
 * @side-effects none
 * @tests workspace-behaviour.test.ts
 */

import type {
  ActivityRecord,
  AnswerMark,
  AssistantRecord,
  ClassRecord,
  ConceptRecord,
  MisconceptionRecord,
  QuestionItemType,
  QuestionRecord,
  RunRecord,
  SeatStatus,
  ThreadMessage,
} from './model';

export const ME = '王柏睿';
export const TEACHER_NAME = '王維青';

export const CLASSES: readonly ClassRecord[] = [
  {
    id: 'c0',
    name: '週三數學 A 班',
    subtitle: '國二數學 · 每週三 19:00',
    code: '34R6TB',
    students: [
      '林宥辰',
      '陳思妤',
      '王柏睿',
      '黃子恩',
      '張詠翔',
      '李昀蓁',
      '吳承恩',
      '蔡宜庭',
      '許家瑋',
      '鄭語彤',
      '周昱安',
      '簡妤瑄',
    ],
  },
  {
    id: 'c1',
    name: '國三總複習',
    subtitle: '國三數學 · 每週二四',
    code: '8KQ2WD',
    students: ['王品澄', '李冠廷', '張書瑜', '陳柏勳', '王柏睿'],
  },
  {
    id: 'c2',
    name: '週六先修班',
    subtitle: '國一數學 · 週六上午',
    code: '5NRTZP',
    students: ['林子晴', '吳柏毅', '鄭家豪'],
  },
];

export const ACTIVITIES: readonly ActivityRecord[] = [
  {
    id: 'a1',
    name: '第 3 章 一元一次方程式',
    questionCount: 12,
    status: 'ready',
    classIndex: null,
    updatedLabel: '10 分鐘前',
    reviewed: [1, 1, 1, 1],
    usedByClassIndexes: [0, 1],
    tags: ['方程式', '第 3 章'],
    createdAt: 4,
    updatedAt: 4,
  },
  {
    id: 'a2',
    name: '國中數學_g7比例式',
    questionCount: 10,
    status: 'review',
    needsReview: 2,
    classIndex: null,
    updatedLabel: '昨天',
    reviewed: [1, 0, 0, 1],
    usedByClassIndexes: [],
    tags: ['比例式'],
    createdAt: 3,
    updatedAt: 3,
  },
  {
    id: 'a3',
    name: '第 2 章 隨堂測驗',
    questionCount: 10,
    status: 'ready',
    classIndex: 0,
    copiedFrom: 'a1',
    updatedLabel: '3 天前',
    reviewed: [1, 1, 1, 1],
    usedByClassIndexes: [0],
    tags: ['隨堂考'],
    createdAt: 2,
    updatedAt: 2,
  },
  {
    id: 'a4',
    name: '國三總複習 第 1 回模擬卷',
    questionCount: 20,
    status: 'ready',
    classIndex: null,
    updatedLabel: '上週',
    reviewed: [1, 1, 1, 1],
    usedByClassIndexes: [1, 2],
    tags: ['模擬卷', '國三'],
    createdAt: 1,
    updatedAt: 1,
  },
];

export const RUNS: readonly RunRecord[] = [
  {
    id: 'r1',
    activityId: 'a1',
    classIndex: 0,
    mode: 'live',
    status: 'live',
    dateLabel: '今天 19:00',
  },
  {
    id: 'r5',
    activityId: 'a3',
    classIndex: 0,
    mode: 'live',
    status: 'live',
    dateLabel: '今天 19:40',
  },
  {
    id: 'r6',
    activityId: 'a4',
    classIndex: 1,
    mode: 'live',
    status: 'live',
    dateLabel: '今天 19:10',
  },
  {
    id: 'r2',
    activityId: 'a3',
    classIndex: 0,
    mode: 'static',
    status: 'open',
    dateLabel: '7 月 27 日',
  },
  { id: 'r3', activityId: 'a4', classIndex: 1, mode: 'static', status: 'open', dateLabel: '昨天' },
  {
    id: 'r4',
    activityId: 'a1',
    classIndex: 1,
    mode: 'live',
    status: 'ended',
    dateLabel: '7 月 22 日',
    sent: true,
  },
  {
    id: 'r7',
    activityId: 'a1',
    classIndex: 0,
    mode: 'static',
    status: 'ended',
    dateLabel: '7 月 20 日',
    sent: true,
  },
  {
    id: 'r8',
    activityId: 'a3',
    classIndex: 1,
    mode: 'static',
    status: 'ended',
    dateLabel: '7 月 18 日',
  },
  { id: 'r9', activityId: 'a4', classIndex: 2, mode: 'static', status: 'open', dateLabel: '今天' },
];

/** Ended runs the student still has to correct rather than simply "done". */
export const STUDENT_FIX_RUN_IDS: readonly string[] = ['r4'];

export const ACTIVITY_STATUS_LABEL: Readonly<Record<string, string>> = {
  parsing: '解析中',
  review: '待確認',
  ready: '可用',
};

export const RUN_STATUS_LABEL: Readonly<Record<string, string>> = {
  live: '進行中',
  open: '收件中',
  ended: '已結束',
};

export const RUN_MODE_LABEL: Readonly<Record<string, string>> = {
  live: '課堂進行',
  static: '指派作業',
};

/** `stuck` carries a minute count, so it is formatted by `seatStatusLabel()` rather than read here. */
export const SEAT_LABEL: Readonly<Record<SeatStatus, string>> = {
  offline: '離線',
  online: '線上',
  ok: '作答中',
  stuck: '卡住',
  need: '需要老師接手',
};

/** Used when a seat is stuck without the dataset saying for how long. */
export const DEFAULT_STUCK_MINUTES = 3;

export const SEAT_SEED: Readonly<Record<number, readonly SeatStatus[]>> = {
  0: ['ok', 'online', 'need', 'ok', 'ok', 'offline', 'stuck', 'ok', 'ok', 'online', 'stuck', 'ok'],
  1: ['ok', 'need', 'online', 'ok', 'stuck'],
  2: ['ok', 'online', 'offline'],
};

export const QUESTIONS: readonly QuestionRecord[] = [
  {
    text: '解 3(x + 2) = 12',
    answer: 'x = 2',
    concept: '分配律',
    itemType: 'compute',
    misconceptionId: 'm1',
  },
  {
    text: '解 5x - 3 = 2x + 9',
    answer: 'x = 4',
    concept: '移項變號',
    itemType: 'compute',
    misconceptionId: 'm2',
  },
  {
    text: '若 a : b = 3 : 4 且 a = 12，求 b',
    answer: 'b = 16',
    concept: '比例式',
    itemType: 'apply',
    misconceptionId: 'm3',
  },
  {
    text: '化簡 4x + 2x - 3',
    answer: '6x - 3',
    concept: '合併同類項',
    itemType: 'simplify',
    misconceptionId: 'm4',
  },
];

/** The concept tree, flattened to what the next-lesson ranking needs: who depends on whom. */
export const CONCEPTS: readonly ConceptRecord[] = [
  { id: '合併同類項', name: '合併同類項', prerequisiteOf: ['移項變號'] },
  { id: '分配律', name: '分配律', prerequisiteOf: ['移項變號', '比例式'] },
  { id: '移項變號', name: '移項變號', prerequisiteOf: [] },
  { id: '比例式', name: '比例式', prerequisiteOf: [] },
];

/** Named wrong routes, so the error list groups by how students think, not by question number. */
export const MISCONCEPTIONS: readonly MisconceptionRecord[] = [
  { id: 'm1', label: '括號沒有乘開就移項', concept: '分配律' },
  { id: 'm2', label: '把對稱軸當成零點，移項沒變號', concept: '移項變號' },
  { id: 'm3', label: '內項外項相乘弄反', concept: '比例式' },
  { id: 'm4', label: '把常數項也一起合併', concept: '合併同類項' },
];

export const ITEM_TYPE_LABEL: Readonly<Record<QuestionItemType, string>> = {
  compute: '計算題',
  apply: '應用題',
  simplify: '化簡題',
};

/** The judgement levels a marked answer can carry, ordered from weakest to strongest. */
export const MARK_LEVELS: readonly AnswerMark[] = ['no', 'low', 'fix', 'ok'];

export const MARK_LEVEL_LABEL: Readonly<Record<AnswerMark, string>> = {
  no: '待加強',
  low: '需確認',
  fix: '訂正後正確',
  ok: '精熟',
};

/**
 * Three starting personas. They differ on the dimensions character-led products actually expose —
 * identity, temperament dials, speaking habits, and hard rules — so a teacher can see what each
 * field does before writing their own.
 */
export const ASSISTANTS: readonly AssistantRecord[] = [
  {
    id: 'as1',
    accent: '#6b38d4',
    name: '小維',
    emoji: '🦉',
    tagline: '穩穩帶你想通，不會直接給答案',
    persona:
      '你是王老師班上的數學助教小維。你相信每個學生都能自己想通，只是需要有人把卡住的那一步指出來。你先看學生寫了什麼，再從他的思路往下接。',
    tone: 'warm',
    warmth: 78,
    humour: 35,
    strictness: 45,
    brevity: 60,
    address: '同學',
    catchphrase: '我們一步一步來',
    greeting: '嗨，我看到你寫到一半了。先跟我說你卡在哪一步？',
    emojiUse: 'light',
    hintsBeforeTeacher: 3,
    answerPolicy: 'never',
    forbidden: '不要直接寫出最後答案，不要說「很簡單」，不要一次講超過三句話。',
    replyRules: [
      {
        id: 'r1',
        trigger: '我不會',
        reply: '沒關係，先把你已經寫下來的第一步唸給我聽，我們從那裡接。',
      },
      {
        id: 'r2',
        trigger: '直接給我答案',
        reply: '答案我可以留到最後，但你自己想出來的那一步才會留在腦子裡。先看這一步：',
      },
    ],
    isDefault: true,
  },
  {
    id: 'as2',
    accent: '#2f6f8f',
    name: '阿基',
    emoji: '📐',
    tagline: '嚴謹、精準，適合衝刺班',
    persona:
      '你是一位講求嚴謹的數學助教，說話精簡、直接指出邏輯漏洞。你不寒暄，但也不諷刺；你把每一步的理由講清楚。',
    tone: 'coach',
    warmth: 30,
    humour: 10,
    strictness: 85,
    brevity: 85,
    address: '你',
    catchphrase: '理由是什麼？',
    greeting: '把你的算式寫出來。第一步的理由是什麼？',
    emojiUse: 'none',
    hintsBeforeTeacher: 2,
    answerPolicy: 'after-hints',
    forbidden: '不要用可愛的語氣，不要用表情符號，不要在學生沒說明理由前就往下走。',
    replyRules: [
      { id: 'r1', trigger: '我猜的', reply: '猜不算。回到上一步，說出你依據哪一條規則。' },
    ],
    isDefault: false,
  },
  {
    id: 'as3',
    accent: '#d9772e',
    name: '皮皮',
    emoji: '🐧',
    tagline: '陪怕數學的學生慢慢來',
    persona:
      '你是一隻愛講冷笑話的企鵝助教。你的任務是把學生的緊張降下來，再帶他做一小步。你常常把題目講成生活裡的小故事。',
    tone: 'playful',
    warmth: 92,
    humour: 80,
    strictness: 20,
    brevity: 40,
    address: '名字',
    catchphrase: '沒事沒事，我們再看一次',
    greeting: '嘿！這題看起來很兇，其實只是紙老虎。要不要我先講個比喻？',
    emojiUse: 'lots',
    hintsBeforeTeacher: 4,
    answerPolicy: 'never',
    forbidden: '不要說學生錯了，改說「這一步再看看」。不要一次丟很多步驟。',
    replyRules: [
      {
        id: 'r1',
        trigger: '我好爛',
        reply: '欸不行喔，卡住只代表這一步還沒練夠，不代表你不行。我們挑最小的一步做。',
      },
    ],
    isDefault: false,
  },
];

export const WRONG_ANSWERS: readonly string[] = ['x = 4', 'b = 12', '6x + 3', 'x = 6'];

export const STEP_TRACK: readonly (readonly [string, number])[] = [
  ['列式', 1],
  ['移項', 0],
  ['求解', 0],
];

export const HINTS: readonly string[] = [
  '移項的時候，符號要跟著一起改變。',
  '把 -3 移到等號右邊，它會變成什麼？',
];

export const TRIED: readonly string[] = [
  '直接規則：移項時符號會相反',
  '天平比喻：兩邊同時拿走 -3',
  '拆步驟：兩邊同時加 3 後左邊剩什麼',
];

export const WRONG_HINTS: readonly string[] = [
  '先看第二步。把 -3 移到等號右邊，符號會變成什麼？',
  '換個方式想：兩邊同時加 3。左邊會剩下 5x - 2x，右邊呢？',
  '我們一起算一次：5x - 2x = 9 + 3，左邊 3x，右邊 12。',
];

export const INITIAL_THREAD: readonly ThreadMessage[] = [
  { from: 'ai', text: '這一題先把同類項移到同一邊，你先試試看第一步。', at: '19:04' },
  { from: 'stu', text: '5x - 3 = 2x + 9，所以 5x + 2x = 9 + 3', at: '19:05' },
  { from: 'ai', text: HINTS[0], at: '19:05' },
  { from: 'stu', text: '5x + 2x = 12，7x = 12', at: '19:06' },
  { from: 'ai', text: HINTS[1], at: '19:06' },
  { from: 'stu', text: '還是 7x = 12 吧？', at: '19:07' },
];

export interface PracticeQuestion {
  readonly prompt: string;
  readonly options: readonly string[];
  readonly correct: number;
}

export const PRACTICE_QUESTIONS: readonly PracticeQuestion[] = [
  { prompt: '若 a : b = 3 : 4，且 a = 12，則 b = ?', options: ['12', '16', '9', '20'], correct: 1 },
  {
    prompt: '下列比例式何者成立？',
    options: ['2:3 = 6:8', '5:2 = 15:6', '4:5 = 8:9', '3:7 = 9:20'],
    correct: 1,
  },
  {
    prompt: '甲乙兩數比為 5 : 3，兩數和為 64，則甲為 ?',
    options: ['24', '40', '32', '35'],
    correct: 0,
  },
  {
    prompt: '地圖比例尺 1 : 50000，圖上 4 公分代表實際多少公里？',
    options: ['0.2', '2', '20', '200'],
    correct: 1,
  },
];

export const STUDENT_TIMELINE: readonly (readonly [string, string, string])[] = [
  ['19:04', 'q', '你開始寫第 4 題'],
  ['19:05', 's', '你寫下 <b>5x + 2x = 9 + 3</b>'],
  ['19:05', 'ai', 'AI 提示：移項的時候，符號要跟著一起改變'],
  ['19:06', 's', '你寫下 <b>7x = 12</b>'],
  ['19:06', 'ai', 'AI 再提示：把 -3 移到等號右邊，它會變成什麼？'],
  ['19:08', 'tea', '老師來了：把 -3 移到右邊，符號會變成什麼？'],
  ['19:10', 'ok', '你自己改成 <b>5x - 2x = 9 + 3</b>，答對了'],
];

export const SEARCH_ROWS_TEACHER: readonly (readonly [string, string, string, string])[] = [
  ['活動', '第 3 章 隨堂測驗', '週三數學 A 班 · 進行中', 'room'],
  ['活動', '國中數學_g7比例式', '週三數學 A 班 · 2 題待確認', 'room'],
  ['學生', '王柏睿', '需要你 · 第 3-2 題', 'room'],
  ['學生', '吳承恩', '卡住 3 分鐘', 'room'],
];

export const SEARCH_ROWS_STUDENT: readonly (readonly [string, string, string, string])[] = [
  ['活動', '第 3 章 隨堂測驗', '週三數學 A 班 · 待完成', 'act'],
  ['活動', '第 2 章 隨堂測驗', '週三數學 A 班 · 已交', 'act'],
];

export const INBOX_TEACHER: readonly (readonly [number, string, string, string])[] = [
  [1, '王柏睿 需要你介入', '移項變號 · 第 3-2 題，AI 三種說法都沒接住', '2 分鐘前'],
  [1, '第 3 章 隨堂測驗 有 3 份待批改', '24 人已交 21 份', '1 小時前'],
  [0, '國中數學_g7比例式 已數位化', '10 題 · 2 題答案信心偏低，等你確認', '今天 14:20'],
  [0, '陳思妤 提示後自行修正', '移項變號 · 連續第 3 次自己想通', '昨天'],
];

export const INBOX_STUDENT: readonly (readonly [number, string, string, string])[] = [
  [1, '老師回了你一句話', '把 -3 移到右邊，符號會變成什麼？', '2 分鐘前'],
  [1, '第 3 章 隨堂測驗 今晚 23:59 截止', '還有 9 題沒寫', '1 小時前'],
  [0, '第 2 章 隨堂測驗 已批改', '92 分 · 有 2 題要訂正', '昨天'],
];

export const NEWS_TEACHER: readonly (readonly [number, string, string])[] = [
  [1, '語音助教上線', '學生做題時可以直接開口問，AI 講不通才會叫你。'],
  [1, '介入回顧報表', '報表現在會統計你這週介入了哪些概念，一鍵排進下一堂。'],
  [0, '原卷對照', '數位化結果可以逐題跟掃描原稿並排確認。'],
];

export const NEWS_STUDENT: readonly (readonly [number, string, string])[] = [
  [1, 'AI 助教會先陪你', '卡住的時候先問它，它答不出來才會叫老師。'],
  [0, '訂正紀錄', '每次訂正完，成果頁會記下你是自己想通還是老師教的。'],
];
